import React, { useCallback, useContext, useState } from 'react'
import { useFetchCheckUpProperties } from '~/hooks/useFetchCheckUpProperties'
import { CheckUpPropertyMeasurement, IFetchCheckUpPropertiesParams } from '~/interfaces'

interface ICheckUpProviderProps {
  children: React.ReactNode
}

const defaultValue = {
  isLoadingCheckUp: false,
  errorCheckup: false
}

export interface ICheckUpContextHelpers {
  isLoadingCheckUp: boolean
  errorCheckup: string | boolean
}

export interface ICheckUpContextProps extends ICheckUpContextHelpers {
  checkUpProperties: CheckUpPropertyMeasurement[]
  getCheckUpProperties: (params: IFetchCheckUpPropertiesParams) => void
  updateCheckUpPropertiesFilters: (updatedFilters: IFetchCheckUpPropertiesParams['queryParams']) => void
}

const CheckUpContext = React.createContext<ICheckUpContextProps>({ ...defaultValue } as ICheckUpContextProps)

const CheckUpProvider = ({ children }: ICheckUpProviderProps): JSX.Element => {
  const [checkUpPropertiesParams, setCheckUpPropertiesParams] = useState<IFetchCheckUpPropertiesParams | null>(null)
  const {
    data: checkUpProperties,
    isLoading: isLoadingCheckUp,
    error: errorCheckup
  } = useFetchCheckUpProperties(checkUpPropertiesParams)

  const getCheckUpProperties = useCallback((params: IFetchCheckUpPropertiesParams): void => {
    setCheckUpPropertiesParams(params)
  }, [])

  const updateCheckUpPropertiesFilters = (params: IFetchCheckUpPropertiesParams['queryParams']): void => {
    if (checkUpPropertiesParams) {
      getCheckUpProperties({ ...checkUpPropertiesParams, queryParams: params })
    }
  }

  return (
    <CheckUpContext.Provider
      value={{
        checkUpProperties: checkUpProperties as CheckUpPropertyMeasurement[],
        getCheckUpProperties,
        updateCheckUpPropertiesFilters,
        isLoadingCheckUp,
        errorCheckup: errorCheckup?.message ?? ''
      }}
    >
      {children}
    </CheckUpContext.Provider>
  )
}

const useCheckUp = (): ICheckUpContextProps => {
  return useContext(CheckUpContext)
}

export { CheckUpContext, CheckUpProvider, useCheckUp }
